// material-ui
import { useNavigate, Link, useLocation, matchPath } from 'react-router-dom';
import { useState } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Theme } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

// project imports
import Navigation from './Navigation';
import SimpleBar from 'components/third-party/SimpleBar';
import useAuth from 'hooks/useAuth';
import useRules from 'hooks/useRules';
import usePwaInstall from 'hooks/usePwaInstall';
import { isDesktopApp, isIOS, isMobileDevice } from 'utils/device';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import {
  SidebarContent,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton
} from '../SidebarComponents';

// assets
import UserOutlined from '@ant-design/icons/UserOutlined';
import LogoutOutlined from '@ant-design/icons/LogoutOutlined';
import DownloadOutlined from '@ant-design/icons/DownloadOutlined';
import WindowsOutlined from '@ant-design/icons/WindowsOutlined';
import MobileOutlined from '@ant-design/icons/MobileOutlined';
import ExportOutlined from '@ant-design/icons/ExportOutlined';
import UsbOutlined from '@ant-design/icons/UsbOutlined';

// ==============================|| DRAWER CONTENT ||============================== //

export default function DrawerContent() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { logout } = useAuth();
  const { isExternalClient } = useRules();
  const downLG = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
  const { menuMaster } = useGetMenuMaster();
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);
  const [openInstallDialog, setOpenInstallDialog] = useState(false);
  const [openDriverDialog, setOpenDriverDialog] = useState(false);
  const [driverChoice, setDriverChoice] = useState<'gc420t' | 'zd220' | null>(null);
  const { canInstall, promptInstall } = usePwaInstall();
  const mobileDevice = isMobileDevice();
  const iosDevice = isIOS();
  const desktopApp = isDesktopApp();

  // Verificar se a rota atual corresponde aos caminhos dos itens do footer
  const isAccountSelected = !!matchPath({ path: '/account', end: false }, pathname);

  const handleNavigation = (path: string) => {
    navigate(path);
    if (downLG) {
      handlerDrawerOpen(false);
    }
  };

  const handleLogout = () => {
    setOpenLogoutDialog(true);
  };

  const confirmLogout = () => {
    try {
      logout();
      window.location.href = '/';
    } catch (err) {
      console.error(err);
    }
  };

  const handleCloseDialog = () => {
    setOpenLogoutDialog(false);
  };

  const handleCloseDriverDialog = () => {
    setOpenDriverDialog(false);
    setDriverChoice(null);
  };

  const handleMobileAppClick = async () => {
    // iOS não tem beforeinstallprompt — só dá pra ensinar o passo a passo manual do Safari
    if (iosDevice) {
      setOpenInstallDialog(true);
      return;
    }
    if (canInstall) {
      await promptInstall();
    } else {
      // Prompt nativo ainda não disponível (ou navegador sem suporte) — cai no passo a passo
      setOpenInstallDialog(true);
    }
  };

  return (
    <SimpleBar sx={{
      height: '100%',
      '& .simplebar-content': {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0
      },
      '& .simplebar-wrapper': {
        height: '100%'
      },
      '& .simplebar-scrollbar': {
        zIndex: 2
      }
    }}>
      <SidebarContent>
        <Navigation />
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              icon={<UserOutlined />}
              selected={isAccountSelected}
              onClick={() => handleNavigation('/account')}
            >
              Perfil
            </SidebarMenuButton>
          </SidebarMenuItem>
          {mobileDevice ? (
            <SidebarMenuItem>
              <SidebarMenuButton icon={<DownloadOutlined />} onClick={handleMobileAppClick}>
                App Mobile <MobileOutlined style={{ marginLeft: 4 }} />
              </SidebarMenuButton>
            </SidebarMenuItem>
          ) : (
            <>
              {!desktopApp && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    component="a"
                    href="/downloads/Salamander-Setup.exe"
                    target="_blank"
                    icon={<DownloadOutlined />}
                  >
                    App Desktop <WindowsOutlined style={{ marginLeft: 4 }} />
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              {/* Driver das impressoras de etiquetas é assunto interno de separação/logística — cliente não usa. */}
              {!isExternalClient && (
                <SidebarMenuItem>
                  <SidebarMenuButton icon={<DownloadOutlined />} onClick={() => setOpenDriverDialog(true)}>
                    Driver da Impressora <WindowsOutlined style={{ marginLeft: 4 }} />
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </>
          )}
          <SidebarMenuItem>
            <SidebarMenuButton
              icon={<LogoutOutlined />}
              onClick={handleLogout}
            >
              Logout
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <Dialog
        open={openLogoutDialog}
        onClose={handleCloseDialog}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
      >
        <DialogTitle id="logout-dialog-title">Confirmar Logout</DialogTitle>
        <DialogContent>
          <Typography variant="body1" id="logout-dialog-description">
            Tem certeza que deseja sair do sistema?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog} color="inherit">
            Cancelar
          </Button>
          <Button onClick={confirmLogout} color="error" variant="contained" autoFocus>
            Sair
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openInstallDialog}
        onClose={() => setOpenInstallDialog(false)}
        aria-labelledby="install-dialog-title"
      >
        <DialogTitle id="install-dialog-title">Adicionar à Tela de Início</DialogTitle>
        <DialogContent>
          {iosDevice ? (
            <>
              <Typography variant="body1" gutterBottom>
                1. Toque no ícone de compartilhar <ExportOutlined /> na barra do Safari.
              </Typography>
              <Typography variant="body1" gutterBottom>
                2. Selecione <b>&quot;Adicionar à Tela de Início&quot;</b>.
              </Typography>
              <Typography variant="body1">3. Toque em &quot;Adicionar&quot; no canto superior direito.</Typography>
            </>
          ) : (
            <>
              <Typography variant="body1" gutterBottom>
                1. Abra o menu do navegador (⋮) no canto superior direito.
              </Typography>
              <Typography variant="body1">
                2. Selecione <b>&quot;Adicionar à tela inicial&quot;</b> ou <b>&quot;Instalar app&quot;</b>.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenInstallDialog(false)} variant="contained" autoFocus>
            Entendi
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDriverDialog}
        onClose={handleCloseDriverDialog}
        aria-labelledby="driver-dialog-title"
        maxWidth="xs"
        fullWidth
      >
        {driverChoice === null && (
          <>
            <DialogTitle id="driver-dialog-title">Instalar Driver da Impressora</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Qual impressora você vai instalar?
              </Typography>
              <Stack spacing={1.5} sx={{ mt: 1 }}>
                <Button
                  variant="outlined"
                  onClick={() => setDriverChoice('gc420t')}
                  sx={{ justifyContent: 'flex-start', textAlign: 'left', py: 1.5, px: 2 }}
                >
                  <Box>
                    <Typography variant="subtitle1">GC420T</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Etiquetas de pedido/produto (10x4cm)
                    </Typography>
                  </Box>
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setDriverChoice('zd220')}
                  sx={{ justifyContent: 'flex-start', textAlign: 'left', py: 1.5, px: 2 }}
                >
                  <Box>
                    <Typography variant="subtitle1">ZD220</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Etiquetas por pacote (10x2cm)
                    </Typography>
                  </Box>
                </Button>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={handleCloseDriverDialog} color="inherit">
                Fechar
              </Button>
            </DialogActions>
          </>
        )}

        {driverChoice === 'gc420t' && (
          <>
            <DialogTitle id="driver-dialog-title">Instalar Driver da Impressora GC420T</DialogTitle>
            <DialogContent>
              <Typography variant="body1" gutterBottom>
                Impressora usada para as etiquetas de pedido/produto (10x4cm).
              </Typography>
              <Typography variant="body1" gutterBottom>
                1. Conecte a impressora no cabo USB <UsbOutlined /> e ligue ela <b>antes</b> de instalar.
              </Typography>
              <Typography variant="body1" gutterBottom>
                2. Baixe e execute o instalador abaixo, e vá clicando em <b>Next</b> até aparecer a opção{' '}
                <b>Install Printer</b>.
              </Typography>
              <Typography variant="body1" gutterBottom>
                3. Aceite os termos do contrato de licença.
              </Typography>
              <Typography variant="body1" gutterBottom>
                4. Na lista de impressoras, selecione <b>ZDesigner GK420t (EPL)</b>.
              </Typography>
              <Typography variant="body1" gutterBottom>
                5. Nas opções de porta, role para baixo e selecione <b>USB001</b>.
              </Typography>
              <Typography variant="body1">6. Clique em <b>Concluir</b> para terminar a instalação.</Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setDriverChoice(null)} color="inherit">
                Voltar
              </Button>
              <Button
                component="a"
                href="/downloads/zd51177415-certified.exe"
                target="_blank"
                variant="contained"
                startIcon={<DownloadOutlined />}
                onClick={handleCloseDriverDialog}
                autoFocus
              >
                Baixar Driver
              </Button>
            </DialogActions>
          </>
        )}

        {driverChoice === 'zd220' && (
          <>
            <DialogTitle id="driver-dialog-title">Instalar Driver da Impressora ZD220</DialogTitle>
            <DialogContent>
              <Typography variant="body1" gutterBottom>
                Impressora usada para as etiquetas por pacote (10x2cm).
              </Typography>
              <Typography variant="body1" gutterBottom>
                1. Baixe e execute o instalador abaixo, e vá clicando em <b>Next</b> até a tela pedir pra clicar em{' '}
                <b>Next</b> de novo pra instalar os arquivos do driver.
              </Typography>
              <Typography variant="body1" gutterBottom>
                2. Na tela <b>Important Information</b>, clique em <b>Next</b>.
              </Typography>
              <Typography variant="body1" gutterBottom>
                3. Na última tela, deixe marcada a opção <b>Run the printer installation wizard</b> e clique em{' '}
                <b>Finish</b>.
              </Typography>
              <Typography variant="body1" gutterBottom>
                4. No Assistente de instalação da impressora que abrir, clique em <b>Próximo</b> e depois em{' '}
                <b>Instalar driver de impressora</b>.
              </Typography>
              <Typography variant="body1" gutterBottom>
                5. Aceite o contrato de licenciamento e clique em <b>Próximo</b>.
              </Typography>
              <Typography variant="body1" gutterBottom>
                6. Em <b>Selecionar porta</b>, escolha <b>Porta USB</b>.
              </Typography>
              <Typography variant="body1" gutterBottom>
                7. Conecte a impressora no cabo USB <UsbOutlined /> e ligue ela — o assistente detecta ela
                automaticamente.
              </Typography>
              <Typography variant="body1" gutterBottom>
                8. Confirme o nome (vem preenchido como <b>ZDesigner ZD220-203dpi ZPL</b>) e clique em{' '}
                <b>Instalar</b>.
              </Typography>
              <Typography variant="body1" gutterBottom>
                9. Quando aparecer <b>concluído</b> no resumo, clique em <b>Sair</b>.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Depois de instalar, escolha essa impressora em <b>Configurar impressora → Impressora de pacote (ZD220, 10x2cm)</b>, na tela de Separação.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setDriverChoice(null)} color="inherit">
                Voltar
              </Button>
              <Button
                component="a"
                href="/downloads/zd1062628275-certified.exe"
                target="_blank"
                variant="contained"
                startIcon={<DownloadOutlined />}
                onClick={handleCloseDriverDialog}
                autoFocus
              >
                Baixar Driver
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </SimpleBar>
  );
}
